import { useEffect, useState } from 'react'
import type { OrderDetailResponse } from '@fitting-room/contracts'
import { ApiRequestError, fetchInvoicePdf } from '../../../lib/api'
import { useStaffToken } from '../../../lib/staffToken'

export type InvoicePdf =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready'; readonly blob: Blob; readonly url: string }

/**
 * The invoice PDF the page shows: the same bytes `GET …/invoice.pdf` draws
 * and the email attaches, fetched once for the preview and handed to
 * Download PDF too, so saving it is the file on the screen rather than a
 * second request.
 *
 * Fetched as a blob and shown from an object URL, for the reason the
 * download is: the console's sign-in travels in a header, which an
 * `<object data>` pointing at the API can't carry. The URL is revoked when
 * the page goes, or when a newer PDF replaces it.
 *
 * Fetched again when this page learns the order changed (its `updatedAt`).
 * It does not watch for somebody else editing the order meanwhile: Send
 * draws the PDF afresh at the moment it sends, so the preview is the
 * invoice as of the order this page last loaded.
 */
export function useInvoicePdf(detail: OrderDetailResponse): { readonly pdf: InvoicePdf; readonly retry: () => void } {
  const { token } = useStaffToken()
  const { id, updatedAt } = detail.order
  const [pdf, setPdf] = useState<InvoicePdf>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!token) return
    const controller = new AbortController()
    let url: string | null = null
    setPdf({ kind: 'loading' })
    fetchInvoicePdf(token, id, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return
        url = URL.createObjectURL(blob)
        setPdf({ kind: 'ready', blob, url })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setPdf({
          kind: 'error',
          message: error instanceof ApiRequestError ? error.message : 'Couldn’t make the PDF. Try again in a moment.',
        })
      })
    return () => {
      controller.abort()
      if (url) URL.revokeObjectURL(url)
    }
  }, [token, id, updatedAt, attempt])

  return { pdf, retry: () => setAttempt((n) => n + 1) }
}

/**
 * The invoice as the customer gets it, shown by the browser's own PDF
 * viewer — not an HTML imitation of it that could drift from what is sent.
 * A browser with no PDF viewer in the page — most phones — draws the
 * `<object>`'s fallback instead: a link that opens the PDF on its own.
 */
export function InvoicePreview({
  orderNo,
  pdf,
  onRetry,
}: {
  readonly orderNo: string
  readonly pdf: InvoicePdf
  readonly onRetry: () => void
}) {
  return (
    <section className="st-card min-w-0 overflow-hidden" aria-label="Invoice preview" data-testid="invoice-preview-panel">
      {pdf.kind === 'loading' ? (
        <div className="flex items-center justify-center px-4" style={{ aspectRatio: '8.5 / 11' }} role="status">
          <p className="st-meta text-sm">Making the invoice…</p>
        </div>
      ) : null}
      {pdf.kind === 'error' ? (
        <div className="flex flex-col items-center justify-center gap-3 px-4 text-center" style={{ aspectRatio: '8.5 / 11' }}>
          <p className="st-alert text-sm" role="alert">
            {pdf.message}
          </p>
          <button type="button" className="st-btn-ghost" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}
      {pdf.kind === 'ready' ? (
        <object
          data={`${pdf.url}#navpanes=0&view=FitH`}
          type="application/pdf"
          title={`Invoice ${orderNo}`}
          aria-label={`Invoice ${orderNo}`}
          className="block w-full"
          style={{ aspectRatio: '8.5 / 11', minHeight: '20rem' }}
          data-testid="invoice-preview"
        >
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 py-8 text-center">
            <p className="st-meta text-sm">This browser can&rsquo;t show the PDF in the page.</p>
            <a className="st-btn-ghost" href={pdf.url} target="_blank" rel="noreferrer">
              Open the invoice
            </a>
          </div>
        </object>
      ) : null}
    </section>
  )
}
